import {DisplayTypeEnum, Inches, RenderV2_DisplayItem} from '../render-v2-d';

/**
 * Automatic (CSS-intrinsic) size sentinel used on the display-item contract.
 *
 * Supported for Insignia height and Lino width only. Documents (Doc) must supply
 * finite, non-negative inches; see {@link resolveDocumentSize}.
 */
export const AUTO_DIMENSION = -1 as Inches;

/**
 * Fallback inches when a Doc size is unsupported.
 *
 * Automatic document sizing is not supported. If either Doc axis is automatic
 * (-1), negative, NaN, or infinite, **both** axes resolve to this fallback
 * before frame/mat geometry (no half-resolved 0×8″ slab). Plain-image Docs
 * never use CSS `auto`, so they cannot overflow a collapsed content box, and
 * mat arithmetic never emits negative CSS.
 */
export const UNSUPPORTED_DOCUMENT_DIMENSION_FALLBACK = 0 as Inches;

export type ResolvedDocumentSize = {
    width: Inches;
    height: Inches;
};

export const isAutomaticDimension = (val: number): boolean => val === AUTO_DIMENSION;

/** Finite inches usable as a Doc size (auto sentinel and non-finite values excluded). */
export const isSupportedDocumentDimension = (val: number): boolean => Number.isFinite(val) && val >= 0;

export const hasUnsupportedDocumentDimension = (item: Pick<RenderV2_DisplayItem, 'width' | 'height'>): boolean => !isSupportedDocumentDimension(item.width) || !isSupportedDocumentDimension(item.height);

/**
 * Resolve one Doc axis to a finite, non-negative size before geometry/CSS.
 * Auto (-1), other negatives, NaN, and ±Infinity become the unsupported fallback.
 */
export const resolveDocumentDimension = (val: number): Inches => {
    if (!isSupportedDocumentDimension(val)) {
        return UNSUPPORTED_DOCUMENT_DIMENSION_FALLBACK;
    }

    return val as Inches;
};

/**
 * Resolve both Doc axes; call before any frame, mat, or document CSS arithmetic.
 * One unsupported axis rejects the whole size (both axes → fallback).
 */
export const resolveDocumentSize = (item: Pick<RenderV2_DisplayItem, 'width' | 'height'>): ResolvedDocumentSize => {
    if (hasUnsupportedDocumentDimension(item)) {
        return {
            width: UNSUPPORTED_DOCUMENT_DIMENSION_FALLBACK,
            height: UNSUPPORTED_DOCUMENT_DIMENSION_FALLBACK,
        };
    }

    return {
        width: item.width,
        height: item.height,
    };
};

/**
 * Outer-minus-inset size for mat openings and concentric mat boxes.
 * Never returns a negative, NaN, or infinite value.
 */
export const matOpeningInches = (outer: number, inset: number): Inches => {
    if (!Number.isFinite(outer) || !Number.isFinite(inset)) {
        return UNSUPPORTED_DOCUMENT_DIMENSION_FALLBACK;
    }

    return Math.max(0, outer - inset) as Inches;
};

/** Layout contribution of a display item's width to column sizing (inches). */
export const layoutWidthInches = (item: RenderV2_DisplayItem): number => {
    if (item.displayType === DisplayTypeEnum.Doc) {
        return resolveDocumentSize(item).width;
    }

    return isAutomaticDimension(item.width) ? 0 : Number.isFinite(item.width) ? item.width : 0;
};

/** Layout contribution of a display item's height to row sizing (inches). Docs only. */
export const layoutDocHeightInches = (item: RenderV2_DisplayItem): number => {
    if (item.displayType !== DisplayTypeEnum.Doc) {
        return 0;
    }

    return resolveDocumentSize(item).height;
};

/**
 * CSS width/height for a Doc image (plain or inside a resolved size).
 * Always finite px strings — never CSS `auto`.
 */
export const documentImageSizeStyle = (item: Pick<RenderV2_DisplayItem, 'width' | 'height'>, inchToPx: (val: number) => string): {width: string; height: string} => {
    const {width, height} = resolveDocumentSize(item);

    return {
        width: inchToPx(width),
        height: inchToPx(height),
    };
};

/**
 * Non-Doc display items (Insignia, Lino, …) keep CSS `auto` for automatic axes.
 * Docs use {@link documentImageSizeStyle} instead.
 */
export const nonDocImageSizeStyle = (item: Pick<RenderV2_DisplayItem, 'width' | 'height'>, inchToPx: (val: number) => string): {width: string; height: string} => ({
    width: isAutomaticDimension(item.width) ? 'auto' : inchToPx(Number.isFinite(item.width) ? item.width : 0),
    height: isAutomaticDimension(item.height) ? 'auto' : inchToPx(Number.isFinite(item.height) ? item.height : 0),
});
